import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

function ParticipantLogin() {
    const apiUrl = import.meta.env.VITE_API_URL;
    const [participantNumber, setParticipantNumber] = useState('');
    const [studyCode, setStudyCode] = useState('');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [isFirstLogin, setIsFirstLogin] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { participantLogin } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Extract study code from URL hash
    useEffect(() => {
        const extractStudyCodeFromHash = () => {
            const hash = window.location.hash;
            if (hash && hash.startsWith('#')) {
                const studyCodeFromHash = hash.substring(1); // Remove the # symbol
                console.log('Extracted study code from hash:', studyCodeFromHash);
                setStudyCode(studyCodeFromHash);
            } else {
                console.warn('No study code found in URL hash');
                setError('No study code found in URL. Please use the correct participant link.');
            }
        };

        extractStudyCodeFromHash();
        
        // Listen for hash changes in case the URL changes
        const handleHashChange = () => {
            extractStudyCodeFromHash();
        };
        
        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    const handleParticipantNumberChange = (event) => {
        // Only allow numeric input
        const value = event.target.value.replace(/\D/g, '');
        setParticipantNumber(value);
    };

    const handlePinChange = (event) => {
        setPin(event.target.value);
    };

    const handleConfirmPinChange = (event) => {
        setConfirmPin(event.target.value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Check if this is a first login
            const response = await axios.post(`${apiUrl}/api/participant/first_login`, {
                study_code: studyCode,
                participant_number: participantNumber
            });

            // Updated to handle the new response format
            if (response.data.is_first_login === true) {
                setIsFirstLogin(true);
            } else {
                setIsFirstLogin(false);
            }
        } catch (error) {
            console.error('First login check error:', error);
            setError('Error occurred while checking participant number. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFirstLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        if (pin !== confirmPin) {
            setError('PINs do not match. Please try again.');
            setLoading(false);
            return;
        }

        try {
            // Set PIN with the new participant identification format
            const response = await axios.post(`${apiUrl}/api/participant/set_pin`, {
                study_code: studyCode,
                participant_number: participantNumber,
                pin
            });

            // The API now returns tokens directly after PIN setup
            if (response.data.access_token && response.data.refresh_token) {
                // Use the AuthContext to set user state and redirect
                const userData = {
                    tokens: {
                        access_token: response.data.access_token,
                        refresh_token: response.data.refresh_token
                    },
                    user: response.data.user || { id: response.data.participant_id },
                    user_type: 'participant'
                };
                
                // Use the context to login
                await participantLogin(participantNumber, pin, studyCode, false);
            } else {
                setError('Error occurred during PIN setup. Please try again.');
            }
        } catch (error) {
            console.error('PIN setup error:', error);
            // Display specific error message from backend if available
            const errorMessage = error.response?.data?.error || 
                                 error.response?.data?.message || 
                                 'Error occurred during PIN setup. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            // Use AuthContext's participantLogin method that we fixed
            await participantLogin(participantNumber, pin, studyCode, false);
            // The redirect is handled by the AuthContext
        } catch (error) {
            console.error('Login error:', error);
            setError('Invalid participant ID or PIN. Please check your credentials and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Don't render the form if we don't have a study code yet
    if (!studyCode) {
        return (
            <div className="force-light-mode participant-login">
                <h2>Loading...</h2>
                {error && <p className="error">{error}</p>}
            </div>
        );
    }

    return (
        <div className="force-light-mode participant-login">
            <h2>{isFirstLogin === null ? 'Enter ID' : (isFirstLogin ? 'Create PIN' : 'Enter PIN')}</h2>
            <p className="study-info">Study: {studyCode}</p>
            <form onSubmit={isFirstLogin === null ? handleSubmit : (isFirstLogin ? handleFirstLogin : handleLogin)}>
                <div className="input-group">
                    <label htmlFor="participantNumber">Participant Number:</label>
                    <input
                        type="text"
                        id="participantNumber"
                        value={participantNumber}
                        onChange={handleParticipantNumberChange}
                        inputMode="numeric"
                        pattern="\d*"
                        required
                        className="participant-id-input"
                    />
                </div>
                {isFirstLogin === true && (
                    <>
                        <div className="input-group">
                            <label htmlFor="pin">Create PIN:</label>
                            <input
                                type="password"
                                id="pin"
                                value={pin}
                                onChange={handlePinChange}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                            />
                        </div>
                        <div className="input-group">
                            <label htmlFor="confirmPin">Confirm PIN:</label>
                            <input
                                type="password"
                                id="confirmPin"
                                value={confirmPin}
                                onChange={handleConfirmPinChange}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                required
                            />
                        </div>
                    </>
                )}
                {isFirstLogin === false && (
                    <div className="input-group">
                        <label htmlFor="pin">Enter PIN:</label>
                        <input
                            type="password"
                            id="pin"
                            value={pin}
                            onChange={handlePinChange}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            required
                        />
                    </div>
                )}
                <button type="submit">
                    {isFirstLogin === null ? 'Next' : 'Login'}
                </button>
            </form>
            {error && <p className="error">{error}</p>}
        </div>
    );
}

export default ParticipantLogin;
